<template>
  <div class="tile rounded-none sm:rounded-md shadow-none sm:shadow space-y-4">
    <div class="flex justify-between space-x-4 mb-8">
      <div class="w-36 sm:w-60">
        <SelectSingleList
          v-model="sorting"
          :options="sortingOptions"
          :label="t('components.chapterHistoryList.labelSortBy')"
          :disabled="pending"
        />
      </div>
    </div>

    <template v-if="pending">
      <div v-for="n in 3" class="flex gap-x-4">
        <Skeleton
          class="sticky top-20 h-[6rem] sm:h-[6.5rem] w-[4.29rem] sm:w-[4.65rem] self-start flex-shrink-0 rounded shadow overflow-hidden"
        />
        <div class="flex-grow">
          <div
            class="font-bold flex items-center sticky top-16 z-[1] pb-2 pt-4 pl-2 -ml-2 -mt-4 bg-white dark:bg-neutral-900"
          >
            <Skeleton class="h-4 w-full sm:w-3/4 md:w-1/2" />
          </div>
          <Skeleton v-for="n in 3" class="mb-2 h-[4.5rem] sm:h-[5rem]" />
        </div>
      </div>
    </template>
    <div v-else-if="error">
      {{ error }}
    </div>
    <div
      v-else-if="history"
      v-for="item in history.list"
      class="flex gap-x-4"
      :key="item.title.id"
    >
      <TheNuxtLink
        :href="linkTo('title', item.title.id)"
        class="sticky top-20 h-[6rem] sm:h-[6.5rem] w-[4.29rem] sm:w-[4.65rem] self-start flex-shrink-0 rounded shadow overflow-hidden"
      >
        <AsyncImage
          v-if="item.coverUrl"
          :src="item.coverUrl"
          class="absolute top-0 left-0 h-full w-full object-cover"
        />
        <div
          v-else
          class="absolute top-0 left-0 h-full w-full text-center flex items-center bg-neutral-200 dark:bg-neutral-700 font-bold"
        >
          Cover Not Found
        </div>
      </TheNuxtLink>
      <div class="flex-grow">
        <div
          class="font-bold flex items-center sticky top-16 z-[1] pb-2 pt-4 pl-2 -ml-2 -mt-4 bg-white dark:bg-neutral-900"
        >
          <TheNuxtLink
            :href="linkTo('title', item.title.id)"
            class="line-clamp-1"
          >
            {{ extractFromLocale(locale, item.title?.attributes?.title ?? {}) }}
          </TheNuxtLink>
        </div>
        <ChapterCard
          v-for="chapter in item.chapters"
          class="mb-2"
          :chapter="chapter"
          :title-id="item.title.id"
          :organization="getRelationship(item.title, 'organization', true)"
          :title-cover-url="item.coverUrl!"
          :key="chapter.id"
        >
          <template #contextMenu>
            <div>
              <!--              TODO: add options here-->
            </div>
          </template>
        </ChapterCard>
      </div>
    </div>
    <NamiPagination
      v-model="page"
      :total-pages="Math.ceil((history?.meta.total ?? 0) / limit)"
      keep-route
    />
  </div>
</template>

<script setup lang="ts">
import {
  ALL_CONTENT,
  type ChapterHistoryEntity,
  type ChapterRelation,
  getRelationship,
  type PopulateRelationship,
  Title,
  type TitleEntity,
} from "~/src/api";
import { useQuery } from "@tanstack/vue-query";
import { useChapterHistory } from "~/utils/tanstack/readingHistory";

const config = useRuntimeConfig();
definePageMeta({ title: "History" });

useHead({
  title: "History",
  link: [
    { rel: "canonical", href: `${config.public.baseUrl}/en/library/history` },
  ],
  meta: [
    {
      property: "og:title",
      content: `"History" - ${config.public.siteName}`,
    },
    { name: "description", content: "Your reading history." },
  ],
});

const nuxtApp = useNuxtApp();
const { locale, t } = useI18n();

const { selectLocalizedCover } = useMediaLink();
const { limit, offset, page } = useInternalPagination();

const sorting = ref<"desc" | "asc">("desc");

const sortingOptions = [
  {
    text: t("components.chapterHistoryList.optionNewest"),
    value: "desc",
  },
  {
    text: t("components.chapterHistoryList.optionOldest"),
    value: "asc",
  },
];

const { data: list, isPending: listPending } = useQuery(
  useChapterHistory(
    () => ({
      includes: ["chapter", "title"],
      offset: offset.value,
      limit: limit.value,
      order: {
        timestamp: sorting.value,
      },
    }),
    getTokenOrThrow,
  ),
);

const {
  data: history,
  isPending,
  error,
} = useQuery({
  queryKey: [
    "grouped_chapter_history",
    { authed: true, offset, limit, sorting },
  ],
  queryFn: async () => {
    if (!list.value) return;
    const groupedByTitle = await getGroupedChaptersByTitleFromReadingHistory(
      list.value.data,
    );
    return {
      list: groupedByTitle,
      meta: list.value.meta,
    };
  },
  enabled: () => !!list.value,
});

const pending = computed(() => listPending.value || isPending.value);

async function getGroupedChaptersByTitleFromReadingHistory(
  readingHistory: ChapterHistoryEntity[],
) {
  const titles = await getDedupedTitlesFromReadingHistory(readingHistory);

  const groupedHistory: {
    title: TitleEntity;
    coverUrl?: string;
    chapters: PopulateRelationship<ChapterRelation>[];
  }[] = [];

  readingHistory.forEach((entry) => {
    const chapter = getRelationship(entry, "chapter", true);

    // Altering the original chapter's publish date
    // so it shows the date when the chapter is read instead
    // TODO: consider whether this side effect is necessary
    chapter.attributes.publishAt = new Date(
      entry.attributes.timestamp * 1000,
    ).toISOString();

    addEntryToGroupedHistory(entry);
  });

  function addEntryToGroupedHistory(entry: ChapterHistoryEntity) {
    const title = getRelatedTitleForEntry(entry);
    const chapter = getRelationship(entry, "chapter", true);

    // Do some minimal title grouping when we can for adjacent chapters
    const previousEntry = groupedHistory.at(groupedHistory.length - 1);
    const isSameTitleAsPreviousEntry = previousEntry?.title.id === title.id;

    if (isSameTitleAsPreviousEntry) {
      previousEntry?.chapters.push(chapter);
    } else {
      groupedHistory.push({
        title: title,
        coverUrl: selectLocalizedCover(locale.value, title, "256"),
        chapters: [chapter],
      });
    }
  }

  function getRelatedTitleForEntry(entry: ChapterHistoryEntity) {
    const relatedTitleId = getRelationship(entry, "title").id;
    return titles.findOrThrow((title) => title.id === relatedTitleId);
  }

  return groupedHistory;
}

async function getDedupedTitlesFromReadingHistory(
  readingHistory: ChapterHistoryEntity[],
) {
  const titleIds = readingHistory
    .map((x) => getRelationship(x, "title").id)
    .dedupe();

  // !!! IMPORTANT !!! We make the assumption that all chapters in a title will belong to the same organization that created the title
  return await Title.search({
    ids: titleIds,
    includes: ["cover_art", "organization"],
    contentRating: ALL_CONTENT,
    limit: titleIds.length,
  }).then((res) => res.data);
}
</script>
